import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPinned, Plus, Trash2, Save, ClipboardCheck, ListChecks, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import ControleQualiteTab from './ControleQualiteTab';
import { AdminPageHeader, adminPageStackClass, adminTabsListProps } from './AdminChrome';

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const emptyDraft = () => ({
  id: null,
  name: '',
  description: '',
  themes: [],
});

const VisiteTerrainPage = ({ token, apiBase, theme, onUnauthorized }) => {
  const [mainTab, setMainTab] = useState('controle');
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteFormOpen, setDeleteFormOpen] = useState(false);
  const [deleteThemeId, setDeleteThemeId] = useState(null);
  const [deleteCriterion, setDeleteCriterion] = useState(null);
  const [duplicating, setDuplicating] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const handleApiError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      const msg =
        error.response?.data?.detail ||
        error.message ||
        'Une erreur est survenue';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    },
    [onUnauthorized]
  );

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/qc-forms`, {
        headers: authHeaders,
      });
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      handleApiError(e);
    } finally {
      setListLoading(false);
    }
  }, [apiBase, token, handleApiError]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) {
      setDraft(emptyDraft());
      setDetailLoading(false);
      return;
    }
    let alive = true;
    setDetailLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(
          `${apiBase}/admin/qc-forms/${selectedId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!alive) return;
        setDraft({
          id: data.id,
          name: data.name || '',
          description: data.description || '',
          themes: (data.themes || []).map((t) => ({
            id: t.id,
            name: t.name || '',
            criteria: (t.criteria || []).map((c) => ({
              id: c.id,
              name: c.name || '',
            })),
          })),
        });
      } catch (e) {
        if (!alive) return;
        handleApiError(e);
        setSelectedId(null);
        setDraft(emptyDraft());
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedId, apiBase, token, handleApiError]);

  const createForm = async () => {
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/qc-forms`,
        { name: 'Nouveau formulaire', description: '' },
        { headers: authHeaders }
      );
      toast.success('Formulaire créé');
      await loadList();
      setSelectedId(data.id);
    } catch (e) {
      handleApiError(e);
    }
  };

  const duplicateForm = async (formId) => {
    if (!formId || duplicating) return;
    setDuplicating(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/qc-forms/${formId}/duplicate`,
        {},
        { headers: authHeaders }
      );
      toast.success('Formulaire dupliqué');
      await loadList();
      setSelectedId(data.id);
    } catch (e) {
      handleApiError(e);
    } finally {
      setDuplicating(false);
    }
  };

  const saveForm = async () => {
    if (!draft.id) return;
    if (!draft.name.trim()) {
      toast.error('Le nom du formulaire est obligatoire');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${apiBase}/admin/qc-forms/${draft.id}`,
        {
          name: draft.name.trim(),
          description: draft.description.trim(),
          themes: draft.themes.map((t) => ({
            id: t.id,
            name: t.name,
            criteria: t.criteria.map((c) => ({
              id: c.id,
              name: c.name,
            })),
          })),
        },
        { headers: authHeaders }
      );
      setDraft({
        id: data.id,
        name: data.name || '',
        description: data.description || '',
        themes: (data.themes || []).map((t) => ({
          id: t.id,
          name: t.name || '',
          criteria: (t.criteria || []).map((c) => ({
            id: c.id,
            name: c.name || '',
          })),
        })),
      });
      toast.success('Formulaire enregistré');
      await loadList();
    } catch (e) {
      handleApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteForm = async () => {
    if (!draft.id) return;
    try {
      await axios.delete(`${apiBase}/admin/qc-forms/${draft.id}`, {
        headers: authHeaders,
      });
      toast.success('Formulaire supprimé');
      setDeleteFormOpen(false);
      setSelectedId(null);
      setDraft(emptyDraft());
      await loadList();
    } catch (e) {
      handleApiError(e);
    }
  };

  const addTheme = () => {
    setDraft((d) => ({
      ...d,
      themes: [
        ...d.themes,
        { id: newId(), name: 'Nouveau thème', criteria: [] },
      ],
    }));
  };

  const updateThemeName = (themeId, name) => {
    setDraft((d) => ({
      ...d,
      themes: d.themes.map((t) => (t.id === themeId ? { ...t, name } : t)),
    }));
  };

  const removeTheme = (themeId) => {
    setDraft((d) => ({
      ...d,
      themes: d.themes.filter((t) => t.id !== themeId),
    }));
    setDeleteThemeId(null);
  };

  const addCriterion = (themeId) => {
    setDraft((d) => ({
      ...d,
      themes: d.themes.map((t) =>
        t.id === themeId
          ? {
              ...t,
              criteria: [
                ...t.criteria,
                { id: newId(), name: '' },
              ],
            }
          : t
      ),
    }));
  };

  const updateCriterion = (themeId, critId, patch) => {
    setDraft((d) => ({
      ...d,
      themes: d.themes.map((t) =>
        t.id === themeId
          ? {
              ...t,
              criteria: t.criteria.map((c) =>
                c.id === critId ? { ...c, ...patch } : c
              ),
            }
          : t
      ),
    }));
  };

  const removeCriterion = (themeId, critId) => {
    setDraft((d) => ({
      ...d,
      themes: d.themes.map((t) =>
        t.id === themeId
          ? { ...t, criteria: t.criteria.filter((c) => c.id !== critId) }
          : t
      ),
    }));
    setDeleteCriterion(null);
  };

  const surfaceStyle = {
    backgroundColor: theme.surface,
    borderColor: theme.border,
  };

  return (
    <div className={`${adminPageStackClass} sm:space-y-6`}>
      <AdminPageHeader
        theme={theme}
        icon={MapPinned}
        title="Visite terrain"
        subtitle="Formulaires de contrôle qualité et saisie sur le terrain."
      />

      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full min-w-0">
        <TabsList {...adminTabsListProps(theme, 'grid w-full grid-cols-2 h-auto')}>
          <TabsTrigger value="controle" className="gap-2 py-2">
            <ClipboardCheck className="h-4 w-4 shrink-0" />
            Contrôle qualités
          </TabsTrigger>
          <TabsTrigger value="gestion" className="gap-2 py-2">
            <ListChecks className="h-4 w-4 shrink-0" />
            Gestion formulaires
          </TabsTrigger>
        </TabsList>

        <TabsContent value="controle" className="mt-6">
          <ControleQualiteTab
            token={token}
            apiBase={apiBase}
            theme={theme}
            onUnauthorized={onUnauthorized}
            isActive={mainTab === 'controle'}
          />
        </TabsContent>

        <TabsContent value="gestion" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Card
              className="lg:col-span-4 border shadow-sm rounded-2xl"
              style={surfaceStyle}
            >
              <CardHeader
                className="space-y-0 pb-4 border-b"
                style={{ borderColor: theme.border }}
              >
                <div className="flex items-center justify-between gap-2">
                  <CardTitle
                    className="text-lg"
                    style={{ color: theme.text }}
                  >
                    Formulaires
                  </CardTitle>
                  <Button
                    size="sm"
                    className="rounded-xl text-white shadow-sm"
                    style={{ backgroundColor: theme.accent }}
                    onClick={createForm}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Nouveau
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-5 max-h-[min(70vh,520px)] overflow-y-auto">
                {listLoading ? (
                  <p
                    className="text-sm py-4"
                    style={{ color: theme.textSecondary }}
                  >
                    Chargement…
                  </p>
                ) : list.length === 0 ? (
                  <p
                    className="text-sm py-4"
                    style={{ color: theme.textSecondary }}
                  >
                    Aucun formulaire. Cliquez sur « Nouveau » pour en créer un.
                  </p>
                ) : (
                  list.map((f) => (
                    <div
                      key={f.id}
                      className={`flex items-stretch gap-1 rounded-xl border transition-colors ${
                        selectedId === f.id ? '' : 'hover:bg-muted/50'
                      }`}
                      style={{
                        borderColor: theme.border,
                        backgroundColor:
                          selectedId === f.id ? theme.background : 'transparent',
                        outline: selectedId === f.id ? `2px solid ${theme.accent}` : undefined,
                        outlineOffset: selectedId === f.id ? 2 : undefined,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedId(f.id)}
                        className="min-w-0 flex-1 text-left p-3"
                      >
                        <p
                          className="font-medium truncate"
                          style={{ color: theme.text }}
                        >
                          {f.name}
                        </p>
                        <p
                          className="text-xs mt-1"
                          style={{ color: theme.textSecondary }}
                        >
                          {f.themes_count ?? 0} thème
                          {(f.themes_count ?? 0) !== 1 ? 's' : ''}
                        </p>
                      </button>
                      <div className="flex shrink-0 flex-col justify-center pr-1 py-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 rounded-lg"
                          title="Dupliquer le formulaire"
                          disabled={duplicating}
                          onClick={(e) => {
                            e.stopPropagation();
                            void duplicateForm(f.id);
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card
              className="lg:col-span-8 border shadow-sm rounded-2xl"
              style={surfaceStyle}
            >
              <CardHeader>
                <CardTitle style={{ color: theme.text }}>
                  {selectedId ? 'Édition du formulaire' : 'Sélectionnez un formulaire'}
                </CardTitle>
              </CardHeader>
              <CardContent
                className={`space-y-6 ${
                  selectedId && !detailLoading && draft.id ? 'pb-28 sm:pb-32' : ''
                }`}
              >
                {!selectedId && (
                  <p
                    className="text-sm"
                    style={{ color: theme.textSecondary }}
                  >
                    Choisissez un formulaire dans la liste ou créez-en un nouveau.
                  </p>
                )}
                {selectedId && detailLoading && (
                  <p
                    className="text-sm"
                    style={{ color: theme.textSecondary }}
                  >
                    Chargement du formulaire…
                  </p>
                )}
                {selectedId && !detailLoading && draft.id && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="qc-form-name" style={{ color: theme.text }}>
                        Nom du formulaire
                      </Label>
                      <Input
                        id="qc-form-name"
                        value={draft.name}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, name: e.target.value }))
                        }
                        className="rounded-lg"
                        style={{ borderColor: theme.border }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="qc-form-desc"
                        style={{ color: theme.text }}
                      >
                        Description
                      </Label>
                      <Textarea
                        id="qc-form-desc"
                        value={draft.description}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            description: e.target.value,
                          }))
                        }
                        rows={3}
                        className="rounded-lg resize-y min-h-[80px]"
                        style={{ borderColor: theme.border }}
                        placeholder="Objectif du contrôle, contexte…"
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t" style={{ borderColor: theme.border }}>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-lg"
                        onClick={addTheme}
                        style={{ borderColor: theme.border }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Ajouter un thème
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {draft.themes.length === 0 && (
                        <p
                          className="text-sm"
                          style={{ color: theme.textSecondary }}
                        >
                          Aucun thème. Ajoutez des thèmes pour regrouper vos critères.
                        </p>
                      )}
                      {draft.themes.map((th) => (
                        <div
                          key={th.id}
                          className="rounded-xl border p-4 space-y-3"
                          style={{
                            borderColor: theme.border,
                            backgroundColor: theme.background,
                          }}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <div className="flex-1 space-y-1">
                              <Label
                                className="text-xs"
                                style={{ color: theme.textSecondary }}
                              >
                                Thème
                              </Label>
                              <Input
                                value={th.name}
                                onChange={(e) =>
                                  updateThemeName(th.id, e.target.value)
                                }
                                className="rounded-lg"
                                style={{ borderColor: theme.border }}
                                placeholder="Nom du thème"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 shrink-0"
                              onClick={() => setDeleteThemeId(th.id)}
                              aria-label="Supprimer le thème"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="flex justify-between items-center">
                            <span
                              className="text-sm font-medium"
                              style={{ color: theme.text }}
                            >
                              Critères (note de 0 à 3 au contrôle)
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="rounded-lg h-8"
                              onClick={() => addCriterion(th.id)}
                              style={{ borderColor: theme.border }}
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" />
                              Critère
                            </Button>
                          </div>

                          {th.criteria.length === 0 ? (
                            <p
                              className="text-sm"
                              style={{ color: theme.textSecondary }}
                            >
                              Aucun critère dans ce thème.
                            </p>
                          ) : (
                            <div className="rounded-lg border overflow-hidden" style={{ borderColor: theme.border }}>
                              <Table>
                                <TableHeader>
                                  <TableRow
                                    className="hover:bg-transparent"
                                    style={{ borderColor: theme.border }}
                                  >
                                    <TableHead style={{ color: theme.text }}>
                                      Critère
                                    </TableHead>
                                    <TableHead className="w-[52px]" />
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {th.criteria.map((c) => (
                                    <TableRow
                                      key={c.id}
                                      style={{ borderColor: theme.border }}
                                    >
                                      <TableCell>
                                        <Input
                                          value={c.name}
                                          onChange={(e) =>
                                            updateCriterion(th.id, c.id, {
                                              name: e.target.value,
                                            })
                                          }
                                          className="h-9 rounded-md"
                                          style={{ borderColor: theme.border }}
                                          placeholder="Libellé du critère"
                                        />
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="h-9 w-9 text-red-600"
                                          onClick={() =>
                                            setDeleteCriterion({
                                              themeId: th.id,
                                              critId: c.id,
                                            })
                                          }
                                          aria-label="Supprimer le critère"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {mainTab === 'gestion' && selectedId && !detailLoading && draft.id && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 sm:px-4"
          style={{
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
            paddingTop: '0.5rem',
          }}
        >
          <div
            className="pointer-events-auto flex w-full max-w-3xl flex-wrap items-center justify-center gap-2 rounded-2xl border px-3 py-2.5 shadow-lg backdrop-blur-sm sm:gap-3 sm:px-4 sm:py-3"
            style={{
              backgroundColor: theme.surface,
              borderColor: theme.border,
              boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
            }}
          >
            <Button
              type="button"
              className="rounded-xl text-white shadow-sm"
              style={{ backgroundColor: theme.accent }}
              onClick={saveForm}
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              style={{ borderColor: theme.border, color: theme.text }}
              onClick={() => void duplicateForm(draft.id)}
              disabled={duplicating}
            >
              <Copy className="h-4 w-4 mr-2" />
              {duplicating ? 'Duplication…' : 'Dupliquer'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
              style={{ borderColor: theme.border }}
              onClick={() => setDeleteFormOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={deleteFormOpen} onOpenChange={setDeleteFormOpen}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce formulaire ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive : le formulaire et sa structure (thèmes, critères) seront supprimés de la base.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteForm}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteThemeId}
        onOpenChange={(o) => !o && setDeleteThemeId(null)}
      >
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce thème ?</AlertDialogTitle>
            <AlertDialogDescription>
              Tous les critères rattachés à ce thème seront retirés de l&apos;éditeur (enregistrez le formulaire pour
              appliquer en base).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteThemeId && removeTheme(deleteThemeId)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteCriterion}
        onOpenChange={(o) => !o && setDeleteCriterion(null)}
      >
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce critère ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le critère sera retiré de la liste. Pensez à enregistrer le formulaire pour sauvegarder en base.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() =>
                deleteCriterion &&
                removeCriterion(deleteCriterion.themeId, deleteCriterion.critId)
              }
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VisiteTerrainPage;
